import { ler } from '../realtime/desempenho';
import type { SuporteContexto } from '../api/types';

/*
  Coleta do suporte: o que vai junto do chamado. Tudo aqui é informativo para a equipe e o
  jogador vê cada valor antes de enviar ("O que vai junto"). Nada de chat, senha ou mensagem
  privada. O servidor recorta tudo de novo antes de gravar.
*/

const ERROS_MAX = 20;
const AMOSTRAS_MAX = 300;

const erros: string[] = [];
const latencias: number[] = [];
const quadros: number[] = [];
let quedas = 0;
let instalado = false;
const inicio = Date.now();

/** Sem origem nem query: só o caminho do arquivo e a linha. */
function local(arquivo: string | undefined, linha?: number, coluna?: number): string {
  if (!arquivo) return '';
  let caminho = arquivo;
  try {
    caminho = new URL(arquivo, window.location.href).pathname;
  } catch { /* fica o texto cru */ }
  return ` @ ${caminho}${linha ? `:${linha}` : ''}${coluna ? `:${coluna}` : ''}`;
}

function guardar(texto: string) {
  const limpo = texto.replace(/\s+/g, ' ').trim().slice(0, 300);
  if (!limpo) return;
  if (erros[erros.length - 1] === limpo) return;
  erros.push(limpo);
  if (erros.length > ERROS_MAX) erros.shift();
}

/** Liga uma vez, no começo do app: guarda os últimos erros de script e amostra latência e FPS. */
export function instalarColeta(): void {
  if (instalado || typeof window === 'undefined') return;
  instalado = true;
  window.addEventListener('error', e => {
    const msg = e.error instanceof Error ? `${e.error.name}: ${e.error.message}` : e.message;
    guardar(`${msg}${local(e.filename, e.lineno, e.colno)}`);
  });
  window.addEventListener('unhandledrejection', e => {
    const r: unknown = e.reason;
    guardar(r instanceof Error ? `unhandledrejection ${r.name}: ${r.message}` : `unhandledrejection ${String(r)}`);
  });
  window.setInterval(() => {
    const s = ler();
    if (s.esperaMs > 0) amostrar(latencias, s.esperaMs);
    if (s.fps > 0 && document.visibilityState === 'visible') amostrar(quadros, s.fps);
  }, 2000);
}

function amostrar(lista: number[], v: number) {
  lista.push(v);
  if (lista.length > AMOSTRAS_MAX) lista.shift();
}

/** O hub caiu e está reconectando. */
export function anotarQueda(): void {
  quedas++;
}

function media(lista: readonly number[]): number | undefined {
  if (lista.length === 0) return undefined;
  return Math.round(lista.reduce((a, b) => a + b, 0) / lista.length);
}

function gpu(): string | undefined {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    const ext = gl?.getExtension('WEBGL_debug_renderer_info');
    const v = ext && gl ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
    return v || undefined;
  } catch {
    return undefined;
  }
}

let gpuCache: string | undefined | null = null;

export interface Navegador {
  nome: string;
  sistema: string;
}

/** Leitura humana do user-agent. O servidor lê de novo pelo cabeçalho; esta é só para o jogador ver. */
export function navegador(ua = navigator.userAgent): Navegador {
  const m = ua.match(/(Edg|OPR|SamsungBrowser|FxiOS|CriOS|Firefox|Chrome|Version)\/(\d+)/);
  const nomes: Record<string, string> = {
    Edg: 'Edge', OPR: 'Opera', SamsungBrowser: 'Samsung Internet', FxiOS: 'Firefox (iOS)',
    CriOS: 'Chrome (iOS)', Firefox: 'Firefox', Chrome: 'Chrome'
  };
  const nome = !m ? '?' : `${nomes[m[1]] ?? (/Safari/.test(ua) ? 'Safari' : '?')} ${m[2]}`;
  const android = ua.match(/Android (\d+)/);
  const sistema = /Windows NT 10/.test(ua) ? 'Windows 10/11'
    : /Windows/.test(ua) ? 'Windows'
    : android ? `Android ${android[1]}`
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux' : '?';
  return { nome, sistema };
}

export interface DadosDoJogo {
  modo: 'pc' | 'cel' | 'tablet';
  lugar: string;
  mapa: string;
  x?: number;
  y?: number;
  canal?: number;
  cacando: boolean;
  postura?: string;
  grupo: boolean;
  duelo: boolean;
  versaoFront: string;
  mundo: string | null;
  /** Só avisos do sistema, nunca fala de jogador. */
  diario: readonly { at: number; texto: string }[];
}

export function montarContexto(j: DadosDoJogo): SuporteContexto {
  if (gpuCache === null) gpuCache = gpu();
  const con = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } }).connection;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const hora = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return {
    lugar: j.lugar || undefined,
    mapa: j.mapa || undefined,
    x: j.x,
    y: j.y,
    canal: j.canal,
    cacando: j.cacando,
    postura: j.postura,
    grupo: j.grupo,
    duelo: j.duelo,
    modo: j.modo,
    telaW: Math.round(window.innerWidth),
    telaH: Math.round(window.innerHeight),
    dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    aparelhoW: screen.width,
    aparelhoH: screen.height,
    toque: navigator.maxTouchPoints > 0,
    idioma: navigator.language,
    fuso: Intl.DateTimeFormat().resolvedOptions().timeZone,
    memoria: typeof mem === 'number' ? mem : undefined,
    nucleos: navigator.hardwareConcurrency || undefined,
    gpu: gpuCache ?? undefined,
    latMedia: media(latencias),
    latPior: latencias.length ? Math.round(Math.max(...latencias)) : undefined,
    quedas,
    rede: con?.effectiveType,
    downlink: con?.downlink,
    fpsMedio: media(quadros),
    fpsPior: quadros.length ? Math.round(Math.min(...quadros)) : undefined,
    sessaoMin: Math.round((Date.now() - inicio) / 60000),
    versaoFront: j.versaoFront,
    mundo: j.mundo ?? undefined,
    erros: [...erros],
    diario: j.diario.slice(-6).map(l => ({ t: hora(l.at), m: l.texto.slice(0, 200) }))
  };
}

/** Versão do front: a do build (VITE_VERSAO) ou "dev". */
export const VERSAO_FRONT: string = (import.meta.env.VITE_VERSAO ?? '').trim() || 'dev';

/**
 * Captura só o canvas do mundo, como está agora (a HUD é HTML e não entra). Reduz para até
 * 1280 px de largura e tenta WebP; se o navegador não fizer WebP (Safari), sai JPEG. Abaixa a
 * qualidade até caber em 300 KB. Devolve o data URL para a miniatura e o base64 puro para o envio.
 */
export async function capturarMundo(canvas: HTMLCanvasElement | null): Promise<{ url: string; base64: string } | null> {
  if (!canvas || canvas.width === 0 || canvas.height === 0) return null;
  const escala = Math.min(1, 1280 / canvas.width);
  const w = Math.max(1, Math.round(canvas.width * escala));
  const h = Math.max(1, Math.round(canvas.height * escala));
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const g = off.getContext('2d');
  if (!g) return null;
  try {
    g.drawImage(canvas, 0, 0, w, h);
  } catch {
    return null;
  }
  const limite = 300 * 1024;
  for (const q of [0.82, 0.7, 0.55, 0.4]) {
    let blob = await paraBlob(off, 'image/webp', q);
    if (!blob || blob.type !== 'image/webp') blob = await paraBlob(off, 'image/jpeg', q);
    if (!blob) return null;
    if (blob.size <= limite) {
      const url = await paraDataUrl(blob);
      const virgula = url.indexOf(',');
      return virgula < 0 ? null : { url, base64: url.slice(virgula + 1) };
    }
  }
  return null;
}

function paraBlob(c: HTMLCanvasElement, tipo: string, q: number): Promise<Blob | null> {
  return new Promise(resolve => {
    try {
      c.toBlob(b => resolve(b), tipo, q);
    } catch {
      resolve(null);
    }
  });
}

function paraDataUrl(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}
